import { useState, useEffect, useMemo } from 'react'
import { PurchaseRequest } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { loadPurchaseRequests } from '../lib/api'
import CreatePurchaseRequestForm from '../components/CreatePurchaseRequestForm'
import PurchaseRequestsTable from '../components/PurchaseRequestsTable'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import { getPurchaseStatusLabel, getPurchaseStatusClass } from '../lib/purchaseRequestStatus'
import './VedouciView.css'

interface VedouciViewProps {
  purchaseRole?: string
}

function VedouciView({ purchaseRole = 'zadatel' }: VedouciViewProps) {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const [requests, setRequests] = useState<PurchaseRequest[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    if (user) {
      loadRequestsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, purchaseRole])

  const loadRequestsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const effectiveRole = purchaseRole || 'zadatel'
      const data = await loadPurchaseRequests(user.id, user.role ?? null, effectiveRole)
      setRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí o nákup')
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: !loading,
    onFound: (r) => {
      setStatusFilter(r.status)
      setSearchText(r.subject || '')
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již byla zpracována.', 'error'),
    elementIdFor: (id) => `purchase-${id}`,
  })

  const handleRequestCreated = async () => {
    setShowCreateForm(false)
    await new Promise(resolve => setTimeout(resolve, 500))
    await loadRequestsData()
  }

  // Фильтрация заявок только по поисковому тексту (для статистики)
  const requestsForStats = useMemo(() => {
    if (!requests || requests.length === 0) return []
    if (!searchText.trim()) return requests
    
    const searchLower = searchText.toLowerCase().trim()
    return requests.filter(r => {
      const subject = (r.subject || '').toLowerCase()
      const description = (r.description || '').toLowerCase()
      const reason = (r.reason || '').toLowerCase()
      const supplier = (r.supplier || '').toLowerCase()
      const centerName = (r.center_name || '').toLowerCase()
      const createdByName = (r.created_by_name || '').toLowerCase()
      const notes = (r.notes || '').toLowerCase()
      
      return subject.includes(searchLower) ||
             description.includes(searchLower) ||
             reason.includes(searchLower) ||
             supplier.includes(searchLower) ||
             centerName.includes(searchLower) ||
             createdByName.includes(searchLower) ||
             notes.includes(searchLower)
    })
  }, [requests, searchText])

  // Фильтрация заявок по выбранному статусу и поисковому тексту (для отображения)
  const filteredRequests = useMemo(() => {
    let filtered = requestsForStats
    
    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(r => {
        if (statusFilter === 'pending_leader') return r.status === 'pending_leader'
        if (statusFilter === 'new') return r.status === 'new'
        if (statusFilter === 'deferred') return r.status === 'deferred'
        if (statusFilter === 'approved_by_economist') return r.status === 'approved_by_economist'
        if (statusFilter === 'approved_by_director') return r.status === 'approved_by_director'
        if (statusFilter === 'rejected_by_economist') return r.status === 'rejected_by_economist'
        if (statusFilter === 'rejected_by_director') return r.status === 'rejected_by_director'
        if (statusFilter === 'rejected') return r.status === 'rejected' || r.status === 'rejected_by_leader' || r.status === 'cancelled_by_requester' || r.status === 'cancelled_by_leader' || r.status === 'rejected_by_vedouci'
        if (statusFilter === 'accepted_by_referent') return r.status === 'accepted_by_referent'
        if (statusFilter === 'ordered_by_referent') return r.status === 'ordered_by_referent'
        if (statusFilter === 'completed') return r.status === 'completed'
        return true
      })
    }
    
    return filtered
  }, [requestsForStats, statusFilter])

  const pendingLeaderRequests = useMemo(() => requestsForStats.filter(r => r.status === 'pending_leader'), [requestsForStats])
  const newRequests = useMemo(() => requestsForStats.filter(r => r.status === 'new'), [requestsForStats])
  const deferredRequests = useMemo(() => requestsForStats.filter(r => r.status === 'deferred'), [requestsForStats])
  const approvedByEconomistRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_economist'), [requestsForStats])
  const approvedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_director'), [requestsForStats])
  const rejectedByEconomistRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_economist'), [requestsForStats])
  const rejectedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_director'), [requestsForStats])
  const acceptedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'accepted_by_referent'), [requestsForStats])
  const orderedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'ordered_by_referent'), [requestsForStats])
  const completedRequests = useMemo(() => requestsForStats.filter(r => r.status === 'completed'), [requestsForStats])

  // Функции для пагинации
  const getPaginatedRequests = (items: PurchaseRequest[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }

  const getTotalPages = (items: PurchaseRequest[]) => {
    return Math.ceil(items.length / itemsPerPage)
  }

  // Сбрасываем страницу при изменении фильтра или поиска
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, searchText])

  const getStatusLabel = getPurchaseStatusLabel
  const getStatusClass = getPurchaseStatusClass

  if (showCreateForm) {
    return (
      <div className="vedouci-view">
        <CreatePurchaseRequestForm 
          onCancel={() => setShowCreateForm(false)}
          onSuccess={handleRequestCreated}
        />
      </div>
    )
  }

  return (
    <div className="vedouci-view">
      <div className="vedouci-header">
        <button 
          className="create-request-button"
          onClick={() => setShowCreateForm(true)}
        >
          + Vytvořit novou žádost
        </button>
      </div>

      <div className="view-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className={viewMode === 'cards' ? 'active' : ''}
                onClick={() => setViewMode('cards')}
              >
                Karty
              </button>
              <button 
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
              >
                Tabulka
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Hledat podle textu..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  minWidth: '250px'
                }}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    cursor: 'pointer'
                  }}
                >
                  Vymazat
                </button>
              )}
            </div>
          </div>

          <div className="admin-stats">
            <div 
              className={`stat-card ${statusFilter === 'pending_leader' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'pending_leader' ? null : 'pending_leader')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Čeká na vedoucího</h3>
              <p className="stat-number">{pendingLeaderRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Nové</h3>
              <p className="stat-number">{newRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'approved_by_economist' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'approved_by_economist' ? null : 'approved_by_economist')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Schváleno ekonomem</h3>
              <p className="stat-number">{approvedByEconomistRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'approved_by_director' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'approved_by_director' ? null : 'approved_by_director')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Schváleno ředitelem</h3>
              <p className="stat-number">{approvedByDirectorRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'deferred' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'deferred' ? null : 'deferred')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Odloženo ekonomem</h3>
              <p className="stat-number">{deferredRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'rejected_by_economist' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'rejected_by_economist' ? null : 'rejected_by_economist')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Zamítnuto ekonomem</h3>
              <p className="stat-number">{rejectedByEconomistRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'rejected_by_director' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'rejected_by_director' ? null : 'rejected_by_director')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Zamítnuto ředitelem</h3>
              <p className="stat-number">{rejectedByDirectorRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'accepted_by_referent' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'accepted_by_referent' ? null : 'accepted_by_referent')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Přijato obchodním referentem</h3>
              <p className="stat-number">{acceptedByReferentRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'ordered_by_referent' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'ordered_by_referent' ? null : 'ordered_by_referent')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Objednáno obchodním referentem</h3>
              <p className="stat-number">{orderedByReferentRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Dokončeno</h3>
              <p className="stat-number">{completedRequests.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer' }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{requestsForStats.length}</p>
            </div>
          </div>

      {loading ? (
        <div className="loading">Načítání...</div>
      ) : viewMode === 'table' ? (
        <PurchaseRequestsTable 
          requests={filteredRequests}
          filterType="vedouci"
          hideStatusFilter={true}
          statusFilter={statusFilter}
        />
      ) : (
        <div className="requests-section">
          <h2>
            {statusFilter ? `${getStatusLabel(statusFilter)} (${filteredRequests.length})` : `Všechny žádosti (${filteredRequests.length})`}
          </h2>
          {filteredRequests.length > 0 ? (
            <>
              <div className="requests-list">
                {getPaginatedRequests(filteredRequests, currentPage).map(request => (
                  <PurchaseRequestCard
                    key={request.id}
                    request={request}
                    getStatusLabel={getStatusLabel}
                    getStatusClass={getStatusClass}
                    searchText={searchText}
                    onAction={() => loadRequestsData()}
                  />
                ))}
              </div>
              {getTotalPages(filteredRequests) > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={getTotalPages(filteredRequests)}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredRequests.length}
                />
              )}
            </>
          ) : (
            <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              Žádné žádosti
            </p>
          )}
        </div>
      )}

    </div>
  )
}

export default VedouciView

